import React from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import { PartnerMember, PartnerResource } from "../../api/partners";
import { downloadMembersCsv } from "../../utils/memberCsv";
import { useDialogState } from "../../hooks/useDialogState";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useSnackbar } from "../../hooks/useSnackbar";
import { ConfirmActionDialog } from "../common/ConfirmActionDialog";
import { InfinitePaginationTable } from "../common/InfinitePaginationTable";
import { BulkImportDialog } from "./BulkImportDialog";
import { MemberBadges } from "./MemberBadges";
import { useMemberRowMutation } from "./useMemberRowMutation";

type MemberRow = PartnerMember & { id: string };

interface PartnerMembersDialogProps {
  open: boolean;
  partnerId: string;
  partnerName: string;
  onClose: () => void;
}

export const PartnerMembersDialog: React.FC<PartnerMembersDialogProps> = ({
  open,
  partnerId,
  partnerName,
  onClose,
}) => {
  const { showSnackbar, SnackbarNode } = useSnackbar();
  const bulkDialog = useDialogState();
  const removeConfirm = useDialogState<PartnerMember>();
  const portalRevokeConfirm = useDialogState<PartnerMember>();
  const staffDemoteConfirm = useDialogState<PartnerMember>();
  const isMobile = useIsMobile();

  const [members, setMembers] = React.useState<PartnerMember[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [displayName, setDisplayName] = React.useState("");
  const [adding, setAdding] = React.useState(false);
  const [removingId, setRemovingId] = React.useState<string>();
  // Drives only the revoke-confirm dialog's loading state — separate from
  // the row mutation's own in-flight set (which disables a row's controls)
  // so a grant completing on another row can't make an unrelated open
  // confirm dialog look like it's in flight.
  const [revokingId, setRevokingId] = React.useState<string>();
  // Same reasoning as revokingId, for the staff-demote confirm dialog.
  const [demotingId, setDemotingId] = React.useState<string>();

  const { mutate: mutateMemberRow, isPending: isMemberRowPending } =
    useMemberRowMutation(setMembers, showSnackbar);

  const loadMembers = React.useCallback(async () => {
    setLoading(true);
    try {
      setMembers(await PartnerResource.listMembers(partnerId));
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : "Failed to load members.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [partnerId, showSnackbar]);

  React.useEffect(() => {
    if (open) {
      loadMembers();
    }
  }, [open, loadMembers]);

  const handleAdd = async () => {
    if (!email.trim() || !displayName.trim()) return;
    setAdding(true);
    try {
      const result = await PartnerResource.addMember(partnerId, {
        email: email.trim(),
        displayName: displayName.trim(),
      });
      const note = result.alreadyHadActiveSubscription
        ? " (already had active subscription — promo skipped)"
        : "";
      showSnackbar(`Member ${result.status}${note}.`);
      setEmail("");
      setDisplayName("");
      await loadMembers();
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : "Failed to add member.",
        "error",
      );
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (member: PartnerMember) => {
    setRemovingId(member.appUserId);
    try {
      await PartnerResource.removeMember(partnerId, {
        appUserId: member.appUserId,
      });
      showSnackbar(`${member.email} offboarded.`);
      setMembers((prev) =>
        prev.filter((m) => m.appUserId !== member.appUserId),
      );
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : "Failed to offboard member.",
        "error",
      );
    } finally {
      setRemovingId(undefined);
      removeConfirm.close();
    }
  };

  const handleSetMemberStaff = (member: PartnerMember, isStaff: boolean) => {
    const name = member.displayName || member.email;
    // Demoting a member revokes portal access server-side unless the client
    // already knows for certain they have none — an unresolved (null)
    // Cognito lookup can still be a live group membership, so it's treated
    // the same as "true" here rather than assumed clear. Granting staff, or
    // demoting a member confirmed to have no portal access, never touches it.
    const mayRevokePortalAccess = !isStaff && member.portalAccess !== false;
    return mutateMemberRow({
      appUserId: member.appUserId,
      optimisticPatch: { isStaff },
      // Merge isStaff always, and portalAccess only on a demote that may
      // revoke it — the endpoint's contract returns the whole member row,
      // and a portal-access write settling around the same time must not
      // have its own field clobbered by a stale value in an unrelated
      // response.
      request: () =>
        PartnerResource.setMemberStaff(
          partnerId,
          member.appUserId,
          isStaff,
        ).then((updated) =>
          mayRevokePortalAccess
            ? { isStaff: updated.isStaff, portalAccess: updated.portalAccess }
            : { isStaff: updated.isStaff },
        ),
      // Judge the outcome from the server's returned `portalAccess`, never
      // from `mayRevokePortalAccess` — the write above can leave a member
      // still in the Partners group (e.g. a concurrent grant lands between
      // this method's own Cognito check and its response), and a message
      // claiming access was removed would then contradict the row it's
      // describing.
      successMessage: (result) => {
        if (isStaff) return `Marked ${name} as staff.`;
        if (!mayRevokePortalAccess) return `Removed staff flag from ${name}.`;
        return result.portalAccess === false
          ? `Removed staff flag and portal access from ${name}.`
          : `Removed staff flag from ${name}, but portal access could not be confirmed revoked.`;
      },
      errorMessage: "Failed to update staff flag.",
    });
  };

  const handleDemoteStaff = async (member: PartnerMember) => {
    setDemotingId(member.appUserId);
    try {
      await handleSetMemberStaff(member, false);
    } finally {
      setDemotingId(undefined);
      staffDemoteConfirm.close();
    }
  };

  const handleSetPortalAccess = async (
    member: PartnerMember,
    grant: boolean,
  ) => {
    if (!grant) setRevokingId(member.appUserId);
    try {
      await mutateMemberRow({
        appUserId: member.appUserId,
        // Never touches isStaff — a Staff change settling meanwhile must
        // survive this write failing and rolling back.
        optimisticPatch: { portalAccess: grant },
        request: () =>
          PartnerResource.setPortalAccess(
            partnerId,
            member.appUserId,
            grant,
          ).then((result) => ({
            portalAccess: result.portalAccess,
            isStaff: result.isStaff,
          })),
        // The server's resulting value, not the requested `grant` — they can
        // differ (e.g. a partially-failed Cognito group change).
        successMessage: (result) =>
          result.portalAccess
            ? `Portal access granted to ${member.email}.`
            : `Portal access revoked for ${member.email}.`,
        errorMessage: "Failed to update portal access.",
      });
    } finally {
      // Only the revoke path opened the confirm dialog, so only it should
      // close it — a grant completing on another row must never dismiss a
      // revoke-confirm the operator still has open.
      if (!grant) {
        setRevokingId(undefined);
        portalRevokeConfirm.close();
      }
    }
  };

  const handleExportCsv = React.useCallback(() => {
    downloadMembersCsv(members, partnerName, partnerName);
  }, [members, partnerName]);

  const memberRows: MemberRow[] = members.map((m) => ({
    ...m,
    id: m.appUserId,
  }));

  const memberColumns = [
    {
      id: "displayName",
      label: "Name",
      render: (member: MemberRow) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexWrap: "wrap",
          }}
        >
          <span>{member.displayName || "—"}</span>
          <MemberBadges member={member} />
        </Box>
      ),
    },
    {
      id: "email",
      label: "Email",
    },
    {
      id: "createdAt",
      label: "Created",
      hideOnMobile: true,
      render: (member: MemberRow) =>
        member.createdAt
          ? new Date(member.createdAt).toLocaleDateString()
          : "—",
    },
    {
      id: "isStaff",
      label: "Staff",
      hideOnMobile: true,
      render: (member: MemberRow) => (
        <Checkbox
          size="small"
          checked={member.isStaff ?? false}
          disabled={isMemberRowPending(member.appUserId)}
          onChange={(e) => {
            const checked = e.target.checked;
            // Gate on "not known to be false" rather than "known to be
            // true" — an unresolved Cognito lookup (null) can still be a
            // live group membership, and skipping the confirm because the
            // client can't see it would let a demote revoke access with no
            // warning and no visible change until the dialog reopens.
            if (!checked && member.portalAccess !== false) {
              staffDemoteConfirm.openWith(member);
            } else {
              handleSetMemberStaff(member, checked);
            }
          }}
          inputProps={{ "aria-label": `Staff — ${member.email}` }}
        />
      ),
    },
    {
      id: "portalAccess",
      label: "Portal access",
      hideOnMobile: true,
      render: (member: MemberRow) => {
        const unknown = member.portalAccess == null;
        const switchEl = (
          <Switch
            size="small"
            checked={member.portalAccess === true}
            disabled={unknown || isMemberRowPending(member.appUserId)}
            onChange={(e) => {
              const grant = e.target.checked;
              if (!grant) {
                portalRevokeConfirm.openWith(member);
              } else {
                handleSetPortalAccess(member, true);
              }
            }}
            slotProps={{
              input: {
                role: "switch",
                "aria-label": `Portal access — ${member.email}`,
              },
            }}
          />
        );
        return unknown ? (
          <Tooltip title="Unknown — Cognito lookup failed">
            <span data-testid="portal-access-unknown">{switchEl}</span>
          </Tooltip>
        ) : (
          switchEl
        );
      },
    },
    {
      id: "actions",
      label: "Actions",
      hideOnMobile: true,
      render: (member: MemberRow) => {
        const isRemoving = removingId === member.appUserId;
        return (
          <Tooltip title="Offboard from partner">
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={isRemoving}
                onClick={() => removeConfirm.openWith(member)}
              >
                {isRemoving ? (
                  <CircularProgress size={16} />
                ) : (
                  <DeleteIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <span>Members — {partnerName}</span>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleExportCsv}
              disabled={loading || members.length === 0}
            >
              Export CSV
            </Button>
            <Button
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={bulkDialog.openDialog}
            >
              Bulk Import
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Add member */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              mb: 2,
              alignItems: { xs: "stretch", sm: "flex-start" },
            }}
          >
            <TextField
              size="small"
              label="Email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              size="small"
              label="Display Name"
              fullWidth
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <Button
              variant="contained"
              startIcon={
                adding ? <CircularProgress size={16} /> : <PersonAddIcon />
              }
              onClick={handleAdd}
              disabled={adding || !email.trim() || !displayName.trim()}
              sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            >
              Add
            </Button>
          </Box>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {loading ? "Loading…" : `Members (${members.length})`}
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : members.length === 0 ? (
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ py: 2, textAlign: "center" }}
            >
              No members yet.
            </Typography>
          ) : (
            <InfinitePaginationTable
              columns={memberColumns}
              items={memberRows}
              pageSize={10}
              onLoadNext={() => {}}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <BulkImportDialog
        open={bulkDialog.open}
        partnerId={partnerId}
        partnerName={partnerName}
        onClose={bulkDialog.close}
        onImported={loadMembers}
      />

      <ConfirmActionDialog
        open={removeConfirm.open}
        title="Offboard member?"
        message={
          <>
            <strong>
              {removeConfirm.data?.displayName || removeConfirm.data?.email}
            </strong>{" "}
            will be offboarded from <strong>{partnerName}</strong>: their paid
            access is revoked, they leave the partner's group, the partner tag
            is removed, and an offboarding email may be sent. This cannot be
            undone.
          </>
        }
        confirmLabel="Offboard member"
        loading={removingId !== undefined}
        onClose={removeConfirm.close}
        onConfirm={() => {
          if (removeConfirm.data) handleRemove(removeConfirm.data);
        }}
      />

      <ConfirmActionDialog
        open={portalRevokeConfirm.open}
        title="Revoke portal access?"
        message={
          <>
            <strong>
              {portalRevokeConfirm.data?.displayName ||
                portalRevokeConfirm.data?.email}
            </strong>{" "}
            will lose sign-in access to the partner portal for{" "}
            <strong>{partnerName}</strong>. Their staff flag and membership are
            unaffected.
          </>
        }
        confirmLabel="Revoke access"
        loading={revokingId !== undefined}
        onClose={portalRevokeConfirm.close}
        onConfirm={() => {
          if (portalRevokeConfirm.data)
            handleSetPortalAccess(portalRevokeConfirm.data, false);
        }}
      />

      <ConfirmActionDialog
        open={staffDemoteConfirm.open}
        title="Remove staff flag?"
        message={
          <>
            {staffDemoteConfirm.data?.portalAccess === true ? (
              <>
                This also revokes{" "}
                <strong>
                  {staffDemoteConfirm.data?.displayName ||
                    staffDemoteConfirm.data?.email}
                </strong>
                's portal access to <strong>{partnerName}</strong>.
              </>
            ) : (
              <>
                Whether{" "}
                <strong>
                  {staffDemoteConfirm.data?.displayName ||
                    staffDemoteConfirm.data?.email}
                </strong>{" "}
                has portal access to <strong>{partnerName}</strong> could not be
                confirmed. If they do, removing the staff flag will also revoke
                it.
              </>
            )}
          </>
        }
        confirmLabel="Remove staff flag"
        loading={demotingId !== undefined}
        onClose={staffDemoteConfirm.close}
        onConfirm={() => {
          if (staffDemoteConfirm.data)
            handleDemoteStaff(staffDemoteConfirm.data);
        }}
      />

      {SnackbarNode}
    </>
  );
};
